import { useEffect, useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, Sparkles } from "lucide-react";

// Easing reused across reveals (Apple-ish expo out)
const EASE = [0.16, 1, 0.3, 1];

// Headline word-reveal: each line is an overflow-hidden wrapper, words rise into view
function RevealLine({ children, delay = 0 }) {
  return (
    <span className="block overflow-hidden">
      <motion.span
        initial={{ y: "110%", opacity: 0 }}
        animate={{ y: "0%", opacity: 1 }}
        transition={{ duration: 1.1, delay, ease: EASE }}
        className="inline-block will-change-transform"
      >
        {children}
      </motion.span>
    </span>
  );
}

export default function Hero() {
  const sectionRef = useRef(null);
  const glowRef = useRef(null);

  // Mouse-reactive glow (radial gradient follows cursor)
  useEffect(() => {
    const el = glowRef.current;
    if (!el) return;
    const onMove = (e) => {
      const rect = el.getBoundingClientRect();
      el.style.setProperty("--gx", `${e.clientX - rect.left}px`);
      el.style.setProperty("--gy", `${e.clientY - rect.top}px`);
    };
    el.addEventListener("mousemove", onMove);
    return () => el.removeEventListener("mousemove", onMove);
  }, []);

  // Parallax scroll transforms
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end start"],
  });
  const gridY = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const orbY = useTransform(scrollYProgress, [0, 1], [0, -120]);
  const contentY = useTransform(scrollYProgress, [0, 1], [0, 60]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section
      ref={sectionRef}
      data-testid="hero-section"
      className="relative min-h-[100svh] flex items-center pt-28 pb-24 overflow-hidden"
    >
      {/* Mouse-reactive glow layer */}
      <div
        ref={glowRef}
        className="absolute inset-0 pointer-events-auto"
        style={{
          background:
            "radial-gradient(700px circle at var(--gx, 50%) var(--gy, 30%), rgba(59,130,246,0.10), transparent 45%), #050505",
        }}
      />

      {/* Parallax grid */}
      <motion.div
        style={{ y: gridY }}
        className="absolute inset-0 bg-grid bg-grid-fade pointer-events-none"
      />

      {/* Floating glow orbs */}
      <motion.div
        style={{ y: orbY }}
        className="absolute -top-32 -left-32 w-[480px] h-[480px] glow-blue animate-float-slow pointer-events-none"
      />
      <motion.div
        style={{ y: orbY }}
        className="absolute -bottom-40 -right-32 w-[520px] h-[520px] glow-blue animate-float-slower opacity-70 pointer-events-none"
      />

      {/* Noise */}
      <div className="noise" />

      <motion.div
        style={{ y: contentY, opacity: contentOpacity }}
        className="relative max-w-7xl mx-auto px-6 lg:px-8 w-full"
      >
        {/* Status badge */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: EASE }}
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/10 bg-white/[0.03] backdrop-blur-md"
          data-testid="hero-badge"
        >
          <span className="relative flex h-1.5 w-1.5">
            <span className="absolute inline-flex h-full w-full rounded-full bg-blue-500 opacity-60 animate-ping" />
            <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-blue-400" />
          </span>
          <span className="text-[11px] uppercase tracking-[0.18em] text-zinc-300 font-medium">
            Verfügbar für neue Projekte
          </span>
        </motion.div>

        {/* Headline with line-by-line reveal */}
        <h1
          data-testid="hero-headline"
          className="mt-7 font-display font-medium text-white text-[44px] sm:text-6xl md:text-[80px] leading-[0.98] tracking-tight max-w-5xl"
        >
          <RevealLine delay={0.05}>Websites, die</RevealLine>
          <RevealLine delay={0.18}>
            <span className="text-gradient-blue">modern aussehen</span>
          </RevealLine>
          <RevealLine delay={0.31}>und Ergebnisse liefern.</RevealLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.55, ease: EASE }}
          data-testid="hero-subheadline"
          className="mt-7 text-base sm:text-lg text-zinc-400 max-w-2xl leading-relaxed"
        >
          Design, Entwicklung und digitale Präsenz für Unternehmen in{" "}
          <span className="text-zinc-200">Apolda</span>,{" "}
          <span className="text-zinc-200">Jena</span> und{" "}
          <span className="text-zinc-200">Weimar</span>.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.7, ease: EASE }}
          className="mt-10 flex flex-wrap gap-3"
        >
          <a
            href="#kontakt"
            data-testid="hero-cta-primary"
            data-cursor="hover"
            className="group relative inline-flex items-center gap-2 px-5 py-3 rounded-full bg-white text-black font-medium text-sm overflow-hidden transition-all duration-500"
          >
            <span className="absolute inset-0 bg-gradient-to-r from-white via-zinc-100 to-white opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
            <span className="relative">Projekt anfragen</span>
            <ArrowUpRight className="relative w-4 h-4 transition-transform duration-500 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
          <a
            href="#arbeiten"
            data-testid="hero-cta-secondary"
            data-cursor="hover"
            className="group inline-flex items-center gap-2 px-5 py-3 rounded-full bg-white/[0.04] border border-white/10 text-white font-medium text-sm hover:bg-white/[0.08] hover:border-white/25 transition-all duration-500 hover:shadow-[0_0_30px_-5px_rgba(59,130,246,0.35)]"
          >
            <Sparkles className="w-4 h-4 text-blue-400 transition-transform duration-500 group-hover:rotate-12" />
            Beispiele ansehen
          </a>
        </motion.div>
      </motion.div>

      {/* Scroll cue */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1.4 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 pointer-events-none"
        aria-hidden="true"
      >
        <span className="text-[10px] tracking-[0.3em] text-zinc-500 uppercase">Scroll</span>
        <span className="relative w-px h-10 overflow-hidden">
          <motion.span
            animate={{ y: ["-100%", "100%"] }}
            transition={{ duration: 2, ease: "easeInOut", repeat: Infinity }}
            className="absolute inset-x-0 top-0 h-full bg-gradient-to-b from-transparent via-blue-400/80 to-transparent"
          />
        </span>
      </motion.div>

      {/* Bottom fade */}
      <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-b from-transparent to-[#050505] pointer-events-none" />
    </section>
  );
}
